import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import { EmptyState } from "../components/EmptyState";
import { ErrorBlock, LoadingBlock, MoneyValue, StatusChip } from "../components/CrmCards";
import { blockingReasonLabel, displayPayableStatus, nextActionLabel } from "../lib/payableLabels";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";
import {
  listPartnerPayables,
  type PartnerPayableRow,
  type PayableTab,
} from "../api/treasuryPayables";

// Treasury Ledger Payout v1 — partner cash payout queue.
//
// Bound to GET /v2/admin/bff/partner-payables[?tab=]. Each card opens a
// detail where the owner can Approve a payout request and Record the
// treasury transfer txHash (the mutations live on the detail). A partner
// with a payable BALANCE but no request yet shows "Available" (not "Ready
// to pay") — there is nothing to action until the partner requests a
// withdrawal. No proposalId / invoiceId / escrow bucket is rendered.

const TABS: ReadonlyArray<{ key: PayableTab; label: string }> = [
  { key: "ready", label: "Ready" },
  { key: "pending", label: "Pending" },
  { key: "paid", label: "Paid" },
  { key: "blocked", label: "Blocked" },
];

function isTab(v: string | null): v is PayableTab {
  return v === "ready" || v === "pending" || v === "paid" || v === "blocked";
}

function PayableCard({ row }: { row: PartnerPayableRow }) {
  const href = `/admin/partner-payables/${encodeURIComponent(row.id)}`;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "14px 16px",
        background: "var(--bg-card)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {row.partner.refCode ?? "—"}
        </div>
        <span style={{ marginLeft: "auto" }}>
          <StatusChip status={displayPayableStatus({ status: row.status, nextAction: row.nextAction })} />
        </span>
      </div>

      <div
        style={{ fontFamily: "monospace", fontSize: 11, color: "var(--fg-dim)" }}
        title={row.identityId}
      >
        {row.identityId}
      </div>

      <MoneyValue display={row.amount.displayAmount} raw={row.amount.rawAmount} size={20} />

      <div style={{ fontSize: 12, color: "var(--fg-dim)" }}>
        Destination:{" "}
        <code style={{ fontSize: 11 }} title={row.destinationShort ?? undefined}>
          {row.destinationShort ?? "—"}
        </code>
      </div>

      <div style={{ fontSize: 12 }}>
        Next action:{" "}
        <strong style={{ color: "var(--fg)" }}>{nextActionLabel(row.nextAction)}</strong>
      </div>

      {row.blockingReason && (
        <div style={{ fontSize: 12, color: "#d23f3f" }}>
          Blocked: {blockingReasonLabel(row.blockingReason)}
        </div>
      )}

      <Link
        to={href}
        style={{
          marginTop: 2,
          fontSize: 12.5,
          fontWeight: 600,
          color: "var(--accent, #2c7be5)",
          textDecoration: "none",
        }}
      >
        View →
      </Link>
    </div>
  );
}

function PartnerPayablesInner() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: PayableTab = isTab(tabParam) ? tabParam : "ready";

  const [rows, setRows] = useState<PartnerPayableRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t: PayableTab) => {
    setError(null);
    setLoading(true);
    try {
      const r = await listPartnerPayables({ tab: t });
      setRows(r.items);
    } catch (e) {
      setError(mapAffiliateError(e).message);
      setRows(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(tab);
  }, [load, tab]);

  function selectTab(next: PayableTab) {
    setSearchParams(next === "ready" ? {} : { tab: next });
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "8px 0 16px 0" }}>
        {TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => selectTab(t.key)}
              aria-pressed={active}
              style={{
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: active ? 700 : 400,
                borderRadius: 999,
                border: active
                  ? "1px solid var(--accent, #2c7be5)"
                  : "1px solid var(--border)",
                color: active ? "var(--accent, #2c7be5)" : "var(--fg)",
                background: active ? "rgba(44,123,229,0.08)" : "var(--bg-card)",
                cursor: "pointer",
              }}
            >
              {t.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => void load(tab)}
          disabled={loading}
          style={{ marginLeft: "auto", padding: "6px 14px", fontSize: 13 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && <ErrorBlock message={error} onRetry={() => void load(tab)} />}

      {!error && loading && rows === null && <LoadingBlock label="Loading payables…" />}

      {!error && rows && rows.length === 0 && (
        <EmptyState
          title={`No ${tab} partner payables.`}
          hint="Partner cash payables appear here once affiliates earn matured rewards. Switch tabs to see other states."
        />
      )}

      {!error && rows && rows.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
          }}
        >
          {rows.map((r) => (
            <PayableCard key={r.id} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PartnerPayables() {
  return (
    <div>
      <PageHeader
        title="Partner payables"
        subtitle="Partner cash payout queue. Approve payout requests and record treasury transfer txHash after Gao Treasury sends USDC."
        method="GET"
        path="/v2/admin/bff/partner-payables"
      />
      <AuthRequired>
        <PartnerPayablesInner />
      </AuthRequired>
    </div>
  );
}
